import { useCallback, useState } from 'react';

export interface UseInlineRowEditResult<TId> {
    editingId: TId | null;
    isPending: boolean;
    beginEdit: (id: TId) => void;
    cancelEdit: () => void;
    commit: (action: () => Promise<void>) => Promise<void>;
}

// Owns the open-row/pending lifecycle shared by every inline tick/cross editor:
// the row stays open and pending swaps in a spinner until `action` resolves;
// a rejection leaves the row open (with `isPending` back to false) for retry.
export function useInlineRowEdit<TId = string>(): UseInlineRowEditResult<TId> {
    const [editingId, setEditingId] = useState<TId | null>(null);
    const [isPending, setIsPending] = useState(false);

    const beginEdit = useCallback((id: TId) => setEditingId(id), []);
    const cancelEdit = useCallback(() => setEditingId(null), []);

    const commit = useCallback(async (action: () => Promise<void>) => {
        setIsPending(true);
        try {
            await action();
            setEditingId(null);
        } finally {
            setIsPending(false);
        }
    }, []);

    return { editingId, isPending, beginEdit, cancelEdit, commit };
}
